/**
 * @email kpozos@ibl.mx
 * @author Karen Yunuen Pozos Arias
 * @jobTitle Full Stack Developer
 * @hook TotalMealsTableApp
 * @description Custom hook to manage state and logic for the **Total Meals Table**.
 * It generates dynamic table headers and rows, manages pagination,
 * dispatches Redux actions to fetch meals data, and updates results.
 *
 * @param {Object} params - Hook parameters.
 * @param {string} params.selectedOption - **Required.** The mode of attendance (daily, weekly, monthly).
 *
 * @returns {Object} Hook state and utilities.
 * @returns {Function} return.t - Translation function from i18next.
 * @returns {string} return.tableId - Unique identifier for the table element.
 * @returns {Array<JSX.Element>} return.thead - Rendered table header cells.
 * @returns {Array<JSX.Element>} return.tbody - Rendered table body rows.
 * @returns {JSX.Element} return.pagination - Pagination controls for the table.
 *
 * @example
 * const { t, thead, tbody, pagination } = TotalMealsTableApp({ selectedOption: ATTENDANCE_MODES.daily });
 */

import React, { useEffect, useState } from 'react';
import { useDispatch } from 'react-redux';
import { TABLES } from "../../../../../Apis/Tables";
import {SearchByFilters, SimpleSearch} from "../../../../../reduxToolkit/Search/SearchApi";
import {CRITERIA_MODEL, SEARCH_MODEL} from "../../../../../components/table/Model/TableModel";
import TableFunction from "../../../../../components/table/TableFunction";
import {
    IconAvatarTable,
    IconClockTable,
    IconMealTable,
    IconScheduleTable
} from "../../../../../components/buttons/IconButtons";

import CustomTh from "../../../../../components/table/CustomTh";
import CustomTd from "../../../../../components/table/CustomTd";
import CustomImageTable from "../../../../../components/CustomImageTable";
import CustomChip from "../../../../../components/CustomChip";
import {colors} from "../../../../../enums/colors";
import {useTranslation} from "react-i18next";
import {ATTENDANCE_MODES, MEAL_CATEGORY} from "../Models/Model";
import {
    currencyFormat,
    EMPTY_VALUES,
    getDateFormat, getFullName,
    getTimeFormat
} from "../../../../../Apis/functions";
import getTimezoneOffset from "../../../../../utils/GetTimeZone";
import GetDateFilter from "../../../Company/Payroll/Functionality/GetDateFilter";
import {GetTotalMealsKpisList} from "../../../../../reduxToolkit/FoodieOrders/FoodieOrdersApi";

const TotalMealsTableApp = ({ selectedOption, setCriteria, finalArray, setBodyForExport, searchElement, prevIsDaily }) => {
    const {t} = useTranslation();
    const dispatch = useDispatch();
    const [modified, setModified] = useState(false);
    const { getAttendanceDate } = GetDateFilter();
    const [tableId, setTableId] = useState("tbl_tmt_total_meals"); // Table ID
    const [results, setResults] = useState(null); // API results
    const [attendanceStartDate, setAttendanceStartDate] = useState(getAttendanceDate(selectedOption).start);
    const [attendanceEndDate, setAttendanceEndDate] = useState( getAttendanceDate(selectedOption).end);
    const option = TABLES.FOODIE_MENU_TOTAL_MEALS;

    /**
     * useEffect lifecycle hook
     * @description Fetches the KPIs list whenever the `selectedOption` changes.
     */
    useEffect(() => {
        let dates = getAttendanceDate(selectedOption);
        let body = {
            ...SEARCH_MODEL,
            attendanceSearchType: selectedOption,
            attendanceTimeZone: getTimezoneOffset(),
            attendanceStartDate: dates.start,
            attendanceEndDate: dates.end,
        };

        dispatch(GetTotalMealsKpisList(body));
    }, [selectedOption]);

    /**
     * @function getThead
     * @description Builds the **table headers** dynamically depending on the selected option (daily vs. others).
     * @returns {Array<JSX.Element>} An array of `<CustomTh>` elements.
     */
    const getThead = () => {
        let columns = [];
        if(selectedOption === ATTENDANCE_MODES.daily){
            columns = columns.concat([
                <CustomTh tableId={tableId} fixedXY={false} left={0} customWidth="150">{t('time')}</CustomTh>
            ]);
        }else{
            columns = columns.concat([
                <CustomTh tableId={tableId} fixedXY={false} left={0} customWidth="150">{t('date_time')}</CustomTh>,
            ]);
        }
        columns = columns.concat([
            <CustomTh tableId={tableId} classes="first_head">{t('name')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('meal_time')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('menu_items')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('meal_category')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('total')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('subsidy')}</CustomTh>,
            <CustomTh tableId={tableId}>{t('remains')}</CustomTh>,
        ]);
        return columns;
    };

    /**
     * @function getTbody
     * @description Builds the **table rows** based on the API results.
     * Adds icons, formatting, and conditional rendering depending on attendance mode.
     *
     * @returns {Object} Object containing:
     * - `body` {Array<JSX.Element>} The generated rows (`<CustomTd>`).
     * - `data` {Array<Object>} The raw data corresponding to rows.
     */
    const getTbody = () => {
        if (!results || results?.content?.length === 0) return {
            body: [],
            data: []
        };

        let body = results?.content?.map((item) => {
            let fullName = getFullName(item?.userName, item?.userLastName, item?.userSecondLastName);
            let category = MEAL_CATEGORY[item?.isExtraRequest] ? t(MEAL_CATEGORY[item?.isExtraRequest]) : EMPTY_VALUES.DEFAULT;
            let columns = [];
            let menuItems = [item.starter?.name, item.side?.name, item.main?.name, item.dessert?.name].filter(name => name && name.trim() !== "");

            // Conditional rendering: Daily mode vs. Date+Time mode
            if(selectedOption === ATTENDANCE_MODES.daily && finalArray?.length <= 0){
                columns = columns.concat([
                    <CustomTd tableId={tableId} fixed={false} left={0} classes={"w-100px"}>
                        { item?.requestDate ?
                            <div className={"d-flex align-items-center"}>
                                <IconClockTable className="icon-color-general-950"/>
                                <p className={"font-color-general-950 fs-14px ml-2"}>
                                    { getTimeFormat(item?.requestDate) }
                                </p>
                            </div>
                            : EMPTY_VALUES.DEFAULT
                        }
                    </CustomTd>
                ]);
            }else{
                columns = columns.concat([
                    <CustomTd tableId={tableId} fixed={false} left={0} classes={"w-100px"}>
                        <div className={" d-flex align-items-center"}>
                            <IconScheduleTable className="icon-color-general-950"/>
                            <p className={"font-color-general-950 fs-14px ml-2"}>
                                {getDateFormat(item?.requestDate,false)}
                            </p>
                        </div>
                        <div className={"d-flex align-items-center"}>
                            <IconClockTable className="icon-color-general-950"/>
                            <p className={"font-color-general-950 fs-14px ml-2"}>
                                {getTimeFormat(item?.requestDate)}
                            </p>
                        </div>
                    </CustomTd>
                ]);
            }

            // Add standard columns
            columns = columns.concat([
                <CustomTd tableId={tableId} classes={"first-column"}>
                    <CustomImageTable image={IconAvatarTable} containerClass={"d-flex align-items-center"} text={fullName}/>
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={colors.secondary100}
                        fontColor={colors.secondary700}
                        borderColor={colors.secondary150}
                        legend={item?.mealTimeName}
                    />
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    { menuItems?.map ((e, index) => {
                        return <div key={`item-${index}`} id={`item-id-${index}`} className={"d-flex align-items-center"}>
                            <div className={"mr-2"}><IconMealTable/></div>
                            {e}
                        </div>
                    })}
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <CustomChip
                        background={colors.secondary100}
                        fontColor={colors.secondary700}
                        borderColor={colors.secondary150}
                        legend={category}
                    />
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    $ {currencyFormat(item.totalMenuCost)}
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <span className={"font-color-error-600 ibl-font-w-600"}>-$ {currencyFormat(item.subsidy)}</span>
                </CustomTd>,
                <CustomTd tableId={tableId}>
                    <span className={"ibl-font-w-600"}>$ {currencyFormat(item.remains)} </span>
                </CustomTd>
            ]);
            return columns;
        });

        return {
            body: body, // Table rows
            data: results?.content // Raw data
        };
    };

    /**
     * Executes a paginated search request using basic criteria.
     *
     * Builds the request body with the current page, page size, selected model,
     * and any additional criteria passed as arguments.
     * Triggers the search action through Redux and updates the results on success.
     */
    const getData = page => {
        const {start, end} = getAttendanceDate(selectedOption);
        let body = SEARCH_MODEL.setObject({
            page,
            size:rowsPerPage,
            model:option,
            key: searchElement
        });

        body = {
            ...body,
            attendanceSearchType: selectedOption,
            attendanceTimeZone: getTimezoneOffset(),
            attendanceStartDate: start,
            attendanceEndDate: end,
        };

        setBodyForExport(body);
        dispatch(SimpleSearch({ body })).then(
            ({ payload: { data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    /**
     * Executes a paginated search request using advanced criteria.
     *
     * Builds the criteria list based on the advanced search (finalArray),
     * appends user criteria, and dispatches the search action through Redux.
     * Updates the results and resets the selected list once the data is returned.
     */
    const getDataAdvancedSearch = (page) => {
        const criteriaList = finalArray.map((item) => {
            return CRITERIA_MODEL.set(item);
        });

        const body = {
            attendanceTimeZone: getTimezoneOffset(),
            attendanceStartDate,
            attendanceEndDate,
            attendanceSearchType: selectedOption,
            pagination: {
                page,
                size: rowsPerPage,
            },
            searchCriteriaList: criteriaList,
        };
        setCriteria(body);
        dispatch(SearchByFilters({ option, body })).then(({payload: {data: { data, success }}}) => {
                if (success === true) {
                    setResults(data);
                }
            },
        );
    }

    // TableFunction manages selection, pagination, and layout
    const { thead,tbody, pagination, page, rowsPerPage} = TableFunction({
        getTbody: getTbody,
        getThead: getThead,
        data: results,
        labelRowsPerPage: 'requests_per_page',
        hasContainer: true,
        fixedPagination: false,

        searchElement, //New
        getData,//New
        modified,//New
        setModified,//New
        getDataAdvancedSearch,//New - apply only with advanced search
        finalArray,//New - apply only with advanced search
    });

    /**
     * useEffect lifecycle hook
     * @description Fetches table data when `page`, `rowsPerPage`, or `selectedOption` change.
     */
    useEffect(() => {
        if(finalArray.length === 0){
            if(!prevIsDaily.current)
                getData(0);
        }
    }, [selectedOption]);

    return {
        t,
        tableId,
        thead,
        tbody,
        results,
        setResults,
        option,
        page,
        rowsPerPage,
        attendanceStartDate,
        attendanceEndDate,
        pagination,
    };
};

export default TotalMealsTableApp;
